/**
 * Manual Page Component
 *
 * Main page displaying handbook/manual with tree sidebar navigation.
 * Features:
 * - Two-column layout (sidebar + content)
 * - URL synchronization with :slug parameter
 * - Responsive design with mobile hamburger menu
 * - Scroll-to-top functionality
 *
 * Story 7-2, Story 1-3 (tree integration), Story 1-5 (layout integration)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { useManualTree, useManualSection } from './useManual';
import { ManualTreeSidebar } from './ManualTreeSidebar';
import { ManualSection } from './ManualSection';
import { ManualOverview } from './ManualOverview';
import { ManualSearch } from './ManualSearch';
import { ScrollToTop } from './ScrollToTop';
import { VersionBadge } from './VersionBadge';

/**
 * Menu icon (hamburger) for mobile
 */
function MenuIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}

/**
 * Close icon (X) for mobile sidebar
 */
function CloseIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

export function ManualPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const contentRef = useRef<HTMLDivElement>(null);

  const { data: tree, isLoading: treeLoading, error: treeError } = useManualTree();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Fetch active section content when slug changes
  const { data: activeSection, isLoading: sectionLoading } = useManualSection(slug || '');

  // Handle navigation - updates URL and closes mobile sidebar
  const handleNavigate = useCallback(
    (targetSlug: string) => {
      navigate(`/handleiding/${targetSlug}`);
      setSidebarOpen(false); // Close sidebar on mobile after navigation
      // Scroll content to top on navigation
      if (contentRef.current) {
        contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    [navigate]
  );

  // Close sidebar on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && sidebarOpen) {
        setSidebarOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [sidebarOpen]);

  // Prevent body scroll when mobile sidebar is open and manage focus
  const sidebarRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden';
      // Focus the sidebar for accessibility when opened on mobile
      sidebarRef.current?.focus();
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [sidebarOpen]);

  const isLoading = treeLoading;
  const error = treeError;

  return (
    <Layout>
      <div data-testid="manual-page" className="flex flex-col h-[calc(100vh-8rem)]">
        {/* Header */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            {/* Mobile menu button */}
            <button
              type="button"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 -ml-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label={sidebarOpen ? 'Sluit menu' : 'Open menu'}
              aria-expanded={sidebarOpen}
              aria-controls="manual-sidebar"
              data-testid="mobile-sidebar-toggle"
            >
              {sidebarOpen ? (
                <CloseIcon className="w-6 h-6" />
              ) : (
                <MenuIcon className="w-6 h-6" />
              )}
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Handleiding</h1>
              <p className="text-gray-600 mt-1 hidden sm:block">
                Gebruikershandleiding voor het Functieloonhuis beoordelingssysteem
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-64 sm:w-72">
              <ManualSearch onNavigate={handleNavigate} />
            </div>
            <VersionBadge />
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12" role="status" aria-live="polite">
            <div className="text-center">
              <div
                className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"
                aria-label="Handleiding wordt geladen"
              />
              <p className="mt-4 text-gray-600">Handleiding laden...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            <p className="font-medium">Fout bij laden van handleiding</p>
            <p className="text-sm mt-1">{(error as Error).message}</p>
          </div>
        )}

        {/* Main Content */}
        {!isLoading && !error && tree && (
          <div className="flex flex-1 overflow-hidden relative">
            {/* Mobile overlay */}
            {sidebarOpen && (
              <div
                className="fixed inset-0 bg-black/50 z-30 lg:hidden"
                onClick={() => setSidebarOpen(false)}
                aria-hidden="true"
                data-testid="sidebar-overlay"
              />
            )}

            {/* Tree Sidebar - w-72 on mobile (wider for touch), w-64 on desktop */}
            <aside
              ref={sidebarRef}
              id="manual-sidebar"
              tabIndex={-1}
              className={`
                fixed lg:static inset-y-0 left-0 z-40
                transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
                lg:translate-x-0 transition-transform duration-200 ease-in-out
                w-72 lg:w-64 flex-shrink-0
                bg-white lg:bg-transparent
                shadow-xl lg:shadow-none
                pt-4 lg:pt-0
              `}
              role="navigation"
              aria-label="Handleiding navigatie"
            >
              <ManualTreeSidebar
                tree={tree}
                activeSlug={slug}
                onNavigate={handleNavigate}
                isLoading={treeLoading}
                error={treeError as Error | null}
              />
            </aside>

            {/* Content Area */}
            <div
              ref={contentRef}
              className="flex-1 overflow-y-auto lg:ml-6"
              data-testid="manual-content-area"
            >
              {sectionLoading && slug ? (
                <div className="bg-white rounded-lg border border-gray-200 p-12 flex items-center justify-center">
                  <div className="text-center">
                    <div
                      className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"
                      aria-label="Sectie laden..."
                    />
                    <p className="mt-4 text-gray-600">Sectie laden...</p>
                  </div>
                </div>
              ) : slug && activeSection ? (
                <ManualSection section={activeSection} onNavigate={handleNavigate} />
              ) : slug && !sectionLoading ? (
                // 404 state - slug provided but section not found
                <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                  <div className="text-6xl mb-4">🔍</div>
                  <h2 className="text-xl font-semibold text-gray-700 mb-2">
                    Sectie niet gevonden
                  </h2>
                  <p className="text-gray-500 max-w-md mx-auto mb-4">
                    De gevraagde sectie &quot;{slug}&quot; kon niet worden gevonden.
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate('/handleiding')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    Terug naar overzicht
                  </button>
                </div>
              ) : (
                <ManualOverview tree={tree ?? []} onNavigate={handleNavigate} />
              )}
            </div>

            {/* Scroll to top button */}
            <ScrollToTop targetRef={contentRef} threshold={300} />
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && (!tree || tree.length === 0) && (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <div className="text-6xl mb-4">📚</div>
            <h2 className="text-xl font-semibold text-gray-700 mb-2">
              Geen handleiding beschikbaar
            </h2>
            <p className="text-gray-500 max-w-md mx-auto">
              Er zijn momenteel geen handleiding secties beschikbaar voor uw rol.
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
}
